import {
  correctionChanges,
  editedCellNodeIds,
  editedHistoricalCells,
  listWrittenFieldIds,
  responseMarks,
  revealedRequiredFieldIds,
  sameAnswer,
  type CompileOptions,
  type ExtraRowCells,
  type FieldValue,
  type MatrixAnswers,
  type ResponseChanges,
  type ResponseMarks,
  type TableAnswers,
  type TableChanges,
} from '@record-fill';
import type { GeneratedRecordCorrectionAnswers, GeneratedRecordForm } from '@/entities/GeneratedRecord';

type CorrectionMatrix = Pick<
  GeneratedRecordForm,
  'structure' | 'rules' | 'frozenExtraRowCounts' | 'lockedFieldIds'
>;

// What the person typed on the normal sheet goes on top of the full photo the correction form
// brings: the fields written since the open, the cells edited in the author's own rows and the rows
// added after the historical ones.
export function mergeUnsavedEdits(
  stored: MatrixAnswers,
  opened: MatrixAnswers,
  current: MatrixAnswers
): MatrixAnswers {
  const values = { ...stored.values };
  for (const fieldId of listWrittenFieldIds(opened, current)) {
    values[fieldId] = current.values[fieldId];
  }

  const edited = editedHistoricalCells(opened, current);
  const tables: Record<string, TableAnswers> = { ...stored.tables };
  for (const [tableId, entry] of Object.entries(current.tables)) {
    const added = entry.extraRows.slice(opened.tables[tableId]?.extraRows.length ?? 0);
    const cells = edited[tableId];
    if (added.length === 0 && !cells) continue;
    const historical = (stored.tables[tableId]?.extraRows ?? []).map((row, rowIndex) =>
      cells?.[rowIndex] ? { ...row, ...cells[rowIndex] } : row
    );
    tables[tableId] = { extraRows: [...historical, ...added] };
  }

  return { ...stored, values, tables };
}

// The lib throws on a sheet a correction could never save (a historical row gone); the sheet
// cannot produce one, and a throw inside a computed would blank the whole page.
function changesSince(stored: MatrixAnswers, current: MatrixAnswers): ResponseChanges | null {
  try {
    return correctionChanges([stored], current);
  } catch {
    return null;
  }
}

export function liveCorrectionMarks(stored: MatrixAnswers, current: MatrixAnswers): ResponseMarks | null {
  const changes = changesSince(stored, current);
  return changes ? responseMarks([stored, changes], 2) : null;
}

export function countCorrectionChanges(marks: ResponseMarks | null): number {
  if (!marks) return 0;
  const cells = Object.values(marks.cells).reduce(
    (total, rows) => total + Object.values(rows).reduce((sum, row) => sum + Object.keys(row).length, 0),
    0
  );
  const rows = Object.values(marks.newRows).reduce((total, indexes) => total + indexes.length, 0);
  return Object.keys(marks.values).length + cells + rows;
}

// The same set the server validates in full on a correction: the fields the body writes, the
// historical cells it edits, and the empty required fields the correction brings into view or
// enables through a rule. Everything else is checked only for shape, so an old row nobody touched
// is not refused for a cell it was saved without, and a required field a rule still hides or
// disables is never charged.
function correctedNodeIds(matrix: CorrectionMatrix, stored: MatrixAnswers, current: MatrixAnswers): string[] {
  const ids = [
    ...listWrittenFieldIds(stored, current),
    ...revealedRequiredFieldIds({
      structure: matrix.structure,
      rules: matrix.rules,
      opened: stored,
      corrected: current,
    }),
  ];
  return [...ids, ...editedCellNodeIds(stored, current)];
}

export function correctionCompileOptions(
  matrix: CorrectionMatrix,
  stored: MatrixAnswers,
  current: MatrixAnswers
): CompileOptions {
  return {
    // The ERP data of an IF or OS is fixed in the ERP and a new record, never by a correction.
    frozenFieldIds: [...matrix.lockedFieldIds],
    frozenExtraRowCounts: matrix.frozenExtraRowCounts,
    reopenHistoricalRows: true,
    validatedFieldIds: [...new Set(correctedNodeIds(matrix, stored, current))],
  };
}

// The body is a diff, so a table brings only its new rows and the historical cells changed, never
// the rows before it: a long history would otherwise outgrow the body limits. A cell compares with
// sameAnswer, as the server does, so one that goes from empty to empty stays out.
export function buildCorrectionSnapshot(
  stored: MatrixAnswers,
  current: MatrixAnswers
): GeneratedRecordCorrectionAnswers {
  const values: MatrixAnswers['values'] = {};
  for (const fieldId of listWrittenFieldIds(stored, current)) {
    values[fieldId] = current.values[fieldId];
  }

  const tables: Record<string, TableChanges> = {};
  for (const [tableId, entry] of Object.entries(changesSince(stored, current)?.tables ?? {})) {
    const historical = stored.tables[tableId]?.extraRows ?? [];
    const editedRows = changedCells(entry.editedRows ?? {}, historical);
    const table: TableChanges = { extraRows: entry.extraRows };
    if (Object.keys(editedRows).length > 0) table.editedRows = editedRows;
    if (table.extraRows.length > 0 || table.editedRows) tables[tableId] = table;
  }

  return { version: current.version, matrixId: current.matrixId, values, tables };
}

function changedCells(
  edited: ExtraRowCells<FieldValue>,
  historical: ReadonlyArray<Record<number, FieldValue>>
): ExtraRowCells<FieldValue> {
  const cells: ExtraRowCells<FieldValue> = {};
  for (const [rowKey, columns] of Object.entries(edited)) {
    for (const [columnKey, value] of Object.entries(columns)) {
      const row = Number(rowKey);
      const column = Number(columnKey);
      if (!sameAnswer(historical[row]?.[column], value)) cells[row] = { ...cells[row], [column]: value };
    }
  }
  return cells;
}
